import React, { useCallback } from "react";
import { useRoomContext } from "@livekit/components-react";
import { useGlobalState } from "@/hooks/useGlobalState";
import { getBrowserMicPermission } from "./utils/get-browser-mic-permission";
import { captureError } from "./utils/logger";
import widgetIcon from "/images/widget-icon.webp";

interface Step1Props {
  setStep: (step: number) => void;
}

const Step1: React.FC<Step1Props> = ({ setStep }) => {
  const { setChatMode, setMicPermission } = useGlobalState();
  const room = useRoomContext();

  const handleChooseChatMode = useCallback(
    async (mode: "voice" | "text") => {
      if (mode === "voice") {
        // Browsers require audio playback to be unlocked from a direct user
        // gesture. Doing this in the voice button handler ensures that the
        // agent's first published audio (including its greeting) is audible.
        try {
          await room.startAudio();
        } catch (error) {
          captureError("Failed to start LiveKit audio playback", error);
        }
      }

      setChatMode(mode);
      setStep(2);

      setMicPermission(await getBrowserMicPermission());
    },
    [room, setChatMode, setMicPermission, setStep],
  );

  const handleChooseVoiceMode = useCallback(() => {
    handleChooseChatMode("voice");
  }, [handleChooseChatMode]);

  const handleChooseTextMode = useCallback(() => {
    handleChooseChatMode("text");
  }, [handleChooseChatMode]);

  const handleWidgetIconClick = useCallback(() => {
    setStep(0);
  }, [setStep]);

  return (
    <div className="nut-flex nut-flex-col nut-gap-[10px] nut-items-end">
      <div className="nut-w-[300px] nut-bg-white nut-rounded-[24px] nut-rounded-br-[8px] nut-border nut-border-gray-200 nut-p-[20px] nut-flex nut-flex-col nut-gap-[18px] nut-shadow-lg">
        <div>
          <span className="nut-inline-block nut-rounded-full nut-bg-[#F2EFFF] nut-text-[#6D5CE7] nut-text-[11px] nut-font-bold nut-px-[9px] nut-py-[4px] nut-mb-[10px]">
            PERSONAL SHOPPING ASSISTANT
          </span>
          <p className="widget-title nut-font-bold nut-text-[22px] nut-leading-[125%] nut-tracking-[0%]">
            What can I help you find?
          </p>
          <p className="nut-text-[13px] nut-leading-[145%] nut-text-gray-500 nut-mt-[7px]">
            Tell me what you need. I’ll ask a few quick questions and recommend
            the best matches from this store.
          </p>
        </div>
        <div className="nut-flex nut-flex-col nut-gap-[16px]">
          <button
            onClick={handleChooseVoiceMode}
            className="voice-chat_button nut-cursor-pointer nut-inline-block nut-w-[100%] nut-text-center nut-pt-[14px] nut-pb-[14px] nut-rounded-[1022px] nut-text-white"
          >
            Start with voice
          </button>
          <div className="text-chat_wrapper nut-w-[100%] nut-p-[1px] nut-rounded-[1022px] nut-flex nut-items-center nut-content-center">
            <button
              onClick={handleChooseTextMode}
              className="nut-cursor-pointer nut-text-chat_button nut-inline-block nut-w-[100%] nut-h-[100%] nut-text-center nut-pt-[14px] nut-pb-[14px] nut-rounded-[1022px] nut-bg-white"
            >
              Type instead
            </button>
          </div>
        </div>
      </div>
      <div className="gradient-wrap nut-w-[58px] nut-h-[57px] nut-bg-white nut-rounded-[50%] nut-p-[2.5px]">
        <button
          onClick={handleWidgetIconClick}
          className="content nut-cursor-pointer nut-flex nut-items-center nut-justify-center"
        >
          <img
            src={widgetIcon}
            alt="widget-icon"
            className="nut-w-[30px] nut-h-[29px]"
          />
        </button>
      </div>
    </div>
  );
};

export default Step1;
